import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { getUserDocuments, getDocumentByFilename, getDownloadUrl } from "../services/uploadFilesApi.jsx";
import { ConfigContext } from "../utils/Config.jsx";

interface Collection {
  files?: any[];
  [key: string]: any;
}

interface S3KeyPage {
  files?: string[];
  folders?: string[];
  last_key?: string;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface AlertConfig {
  open: boolean;
  type: "loading" | "success" | "error" | "confirm";
  message: string;
  title?: string;
  onConfirm?: () => Promise<void>;
}

export const useFileManagement = (isOpen: boolean) => {
  const [collections, setCollections] = useState<{ [key: string]: Collection }>({});
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [defaultCollection, setDefaultCollection] = useState("");
  const [textConfig, setTextConfig] = useState<any>({});
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 25
  });
  const [paginationTokens, setPaginationTokens] = useState<string[]>([]);
  
  const paginationTokensRef = useRef<string[]>([]);
  const itemsPerPageRef = useRef(25);
  const hasInitializedRef = useRef(false);
  
  const { config } = useContext(ConfigContext);

  useEffect(() => {
    paginationTokensRef.current = paginationTokens;
  }, [paginationTokens]);

  useEffect(() => {
    itemsPerPageRef.current = pagination.itemsPerPage;
  }, [pagination.itemsPerPage]);

  const fetchInitialData = async () => {
    if (!isOpen) return;

    setDefaultCollection(config.DEFAULT_COLLECTION);
    try {
      const textResponse = await fetch("/text.json");
      if (textResponse.ok) {
        const textData = await textResponse.json();
        setTextConfig(textData);
      } else {
        setTextConfig({});
      }
    } catch (textError) {
      setTextConfig({});
    }
  };

  const getCurrentDirectory = useCallback((): Collection => {
    let current = collections;
    
    for (const folder of currentPath) {
      if (current[folder]) {
        current = current[folder];
      } else {
        return { files: [] };
      }
    }
    return current || { files: [] };
  }, [collections, currentPath]);

  const ensureFolderExists = useCallback((folderPath: string[], docObj: any = null, targetCollection: { [key: string]: Collection } = {}) => {
    let currentLevel = targetCollection;

    for (let i = 0; i < folderPath.length; i++) {
      const folderName = folderPath[i].trim();
      if (!folderName) continue;

      if (!currentLevel[folderName]) {
        currentLevel[folderName] = { files: [] };
      }
      currentLevel = currentLevel[folderName];
    }

    if (docObj && currentLevel.files) {
      currentLevel.files.push(docObj);
    }

    return currentLevel;
  }, []);

  const fetchUserDocuments = useCallback(async (prefix?: string, setAlertConfig?: (config: AlertConfig) => void, page: number = 1, isSearch: boolean = false, searchTerm?: string) => {
    if (!isOpen) return;

    setIsLoading(true);
    if (setAlertConfig) {
      setAlertConfig({
        open: true,
        type: "loading",
        message: textConfig?.loadingDocuments || "Cargando documentos...",
      });
    }

    try {
      const startKey = page > 1 && paginationTokensRef.current[page - 2] ? paginationTokensRef.current[page - 2] : undefined;
      const response: S3KeyPage = await getUserDocuments(prefix, itemsPerPageRef.current, startKey);

      if (!response) {
        if (setAlertConfig) {
          setAlertConfig({
            open: true,
            type: "error",
            message: textConfig?.errorLoadingDocuments || "Error al cargar documentos",
          });
        }
        return;
      }

      const groupedDocuments: { [key: string]: Collection } = {};

      if (response.files && response.files.length > 0) {
        response.files.forEach((filePath: string) => {
          if (isSearch && searchTerm && filePath !== searchTerm) {
            return;
          }

          const paths = filePath.split("/").filter((p) => p.trim() !== "");
          const fileName = paths[paths.length - 1];

          const fileObj = {
            key: filePath,
            FILE_NAME: filePath, 
            FILE_HASH: filePath,  
            COLLECTION_NAME: paths[0] || defaultCollection,
          };
          
          if (paths.length > 2) {
            const folderPath = paths.slice(0, -1);
            ensureFolderExists(folderPath, fileObj, groupedDocuments);
          } else if (paths.length === 2) {
            const collectionName = paths[0];
            if (!groupedDocuments[collectionName]) {
              groupedDocuments[collectionName] = { files: [] };
            }
            groupedDocuments[collectionName].files!.push(fileObj);
          } else {
            if (!groupedDocuments[defaultCollection]) {
              groupedDocuments[defaultCollection] = { files: [] };
            }
            groupedDocuments[defaultCollection].files!.push(fileObj);
          }
        });
      }

      if (response.folders && response.folders.length > 0) {
        response.folders.forEach((folderPath: string) => {
          const paths = folderPath.split("/").filter((p) => p.trim() !== "");
          
          if (paths.length === 1) {
            const collectionName = paths[0];
            if (!groupedDocuments[collectionName]) {
              groupedDocuments[collectionName] = { files: [] };
            }
          } else if (paths.length > 1) {
            const collectionName = paths[0];
            const folderName = paths[1]; 
            
            if (!groupedDocuments[collectionName]) {
              groupedDocuments[collectionName] = { files: [] };
            }
            
            if (!groupedDocuments[collectionName][folderName]) {
              groupedDocuments[collectionName][folderName] = { files: [] };
            }
            
            if (paths.length > 2) {
              const remainingPaths = paths.slice(2);
              let currentLevel = groupedDocuments[collectionName][folderName];
              
              remainingPaths.forEach((subFolderName) => {
                if (!currentLevel[subFolderName]) {
                  currentLevel[subFolderName] = { files: [] };
                }
                currentLevel = currentLevel[subFolderName];
              });
            }
          }
        });
      }

      setCollections(groupedDocuments);
      setIsSearchMode(isSearch);

      const totalItems = (response.files?.length || 0) + (response.folders?.length || 0);
      const hasMorePages = !!response.last_key;
      
      if (response.last_key) {
        setPaginationTokens(prev => {
          const newTokens = [...prev];
          newTokens[page - 1] = response.last_key!;
          return newTokens;
        });
      }

      const estimatedTotalPages = hasMorePages ? page + 1 : page;
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: Math.max(prev.totalPages, estimatedTotalPages),
        totalItems: totalItems
      }));

    } catch (error: any) {
      if (setAlertConfig) {
        setAlertConfig({
          open: true,
          type: "error",
          message: textConfig?.errorLoadingDocuments || "Error al cargar documentos",
        });
      }
    } finally {
      setIsLoading(false);
      if (setAlertConfig) {
        setAlertConfig({
          open: false,
          type: "loading",
          message: "",
        });
      }
    }
  }, [defaultCollection, textConfig, isOpen, ensureFolderExists]);

  const handlePageChange = useCallback((newPage: number) => {
    const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
    fetchUserDocuments(prefix, undefined, newPage, false, undefined);
  }, [currentPath, fetchUserDocuments]);

  const resetPagination = useCallback(() => {
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 25
    });
    setPaginationTokens([]);
  }, []);

  const handleFolderClick = useCallback((folderName: string) => {
    setCurrentPath((prev) => [...prev, folderName]);
    const newPath = [...currentPath, folderName];
    const prefix = newPath.join("/") + "/";
    resetPagination();
    fetchUserDocuments(prefix, undefined, 1, false, undefined);
  }, [currentPath, fetchUserDocuments, resetPagination]);

  const handleNavigateBack = useCallback(() => {
    setCurrentPath((prev) => {
      const newPath = prev.slice(0, -1);
      const prefix = newPath.length > 0 ? newPath.join("/") + "/" : undefined;
      resetPagination();
      fetchUserDocuments(prefix, undefined, 1, false, undefined);
      return newPath;
    });
  }, [fetchUserDocuments, resetPagination]);

  const handleNavigateToRoot = useCallback(() => {
    setCurrentPath([]);
    resetPagination();
    fetchUserDocuments(undefined, undefined, 1, false, undefined);
  }, [fetchUserDocuments, resetPagination]);

  const handleNavigateToPath = useCallback((path: string[]) => {
    setCurrentPath(path);
    const prefix = path.length > 0 ? path.join("/") + "/" : undefined;
    resetPagination();
    fetchUserDocuments(prefix, undefined, 1, false, undefined);
  }, [fetchUserDocuments, resetPagination]);

  const handleRefresh = useCallback(async (setAlertConfig?: (config: AlertConfig) => void) => {
    setIsRefreshing(true);
    const prefix = currentPath.length > 0 ? currentPath.join("/") + "/" : undefined;
    resetPagination();
    await fetchUserDocuments(prefix, setAlertConfig, 1, false, undefined);
    setIsRefreshing(false);
  }, [fetchUserDocuments, currentPath, resetPagination]);

  const handleSearchByFile = useCallback(async (searchTerm: string, setAlertConfig?: (config: AlertConfig) => void) => {
    setIsSearchMode(true);
    resetPagination();
    
    try {
      if (searchTerm.includes('/')) {
        const document = await getDocumentByFilename(searchTerm);
        
        const parts = searchTerm.split('/');
        const folderPath = parts.slice(0, -1); 
        
        setCurrentPath(folderPath);
        
        const prefix = folderPath.length > 0 ? folderPath.join("/") + "/" : undefined;
        await fetchUserDocuments(prefix, setAlertConfig, 1, true, searchTerm);
      } else {
        await fetchUserDocuments(searchTerm, setAlertConfig, 1, true, searchTerm);
      }
    } catch (error) {
      await fetchUserDocuments(searchTerm, setAlertConfig, 1, true, searchTerm);
    }
  }, [fetchUserDocuments, resetPagination]);

  const handleDownloadFile = useCallback(async (filename: string, setAlertConfig?: (config: AlertConfig) => void) => {
    try {
      if (setAlertConfig) {
        setAlertConfig({
          open: true,
          type: "loading",
          message: textConfig?.downloadingFile || "Abriendo archivo...",
        });
      }

      const downloadUrl = await getDownloadUrl(filename);
      
      window.open(downloadUrl, '_blank');

      if (setAlertConfig) {
        setAlertConfig({
          open: true,
          type: "success",
          message: textConfig?.fileDownloaded || "Archivo abierto exitosamente",
        });
      }
    } catch (error) {
      if (setAlertConfig) {
        setAlertConfig({
          open: true,
          type: "error",
          message: textConfig?.errorDownloadingFile || "Error al abrir el archivo",
        });
      }
    }
  }, [textConfig]);

  const handleClearSearch = useCallback(async (setAlertConfig?: (config: AlertConfig) => void) => {
    setIsSearchMode(false);
    setCurrentPath([]);
    resetPagination();
    await fetchUserDocuments(undefined, setAlertConfig, 1, false, undefined);
  }, [fetchUserDocuments, resetPagination]);

  const handleDeleteCollection = useCallback(async (collectionName: string, setAlertConfig?: (config: AlertConfig) => void) => {
    if (setAlertConfig) {
      setAlertConfig({
        open: true,
        type: "confirm",
        message: textConfig?.confirmDeleteCollection || "¿Estás seguro de que quieres eliminar esta colección?",
        title: textConfig?.deleteCollection || "Eliminar Colección",
        onConfirm: async () => {
          try {
            setCollections((prev) => {
              const newCollections = { ...prev };
              delete newCollections[collectionName];
              return newCollections;
            });
            
            if (setAlertConfig) {
              setAlertConfig({
                open: true,
                type: "success",
                message: textConfig?.collectionDeleted || "Colección eliminada exitosamente",
              });
            }
          } catch (error) {
            if (setAlertConfig) {
              setAlertConfig({
                open: true,
                type: "error",
                message: textConfig?.errorDeletingCollection || "Error al eliminar la colección",
              });
            }
          }
        },
      });
    }
  }, [textConfig]);

  useEffect(() => {
    fetchInitialData();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchUserDocuments(undefined, undefined, 1, false, undefined);
    } else if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, fetchUserDocuments]);

  const truncateFileName = useCallback((fileName: string, maxLength: number = 20) => {
    if (fileName.length <= maxLength) return fileName;
    return fileName.substring(0, maxLength - 3) + "...";
  }, []);

  return {
    collections,
    setCollections,
    currentPath,
    setCurrentPath,
    isLoading,
    isRefreshing,
    defaultCollection,
    textConfig,
    pagination,
    isSearchMode,
    getCurrentDirectory,
    handleFolderClick,
    handleNavigateBack,
    handleNavigateToRoot,
    handleNavigateToPath,
    handleRefresh,
    handleManualRefresh: handleRefresh,
    handleDeleteCollection,
    fetchUserDocuments,
    handlePageChange,
    resetPagination,
    handleSearchByFile,
    handleClearSearch,
    handleDownloadFile,
    truncateFileName,
  };
};